import { IntelligentCheck } from '../kyc/IntelligentCheck'

// Shipper IDs = the backend's CustomerIdentity records: the ID a business
// registers for the customer it ships for (Business API). Routed as
// /admin/shipper-ids rather than "customer-identities" — the sidebar marks
// Customers active for any page containing "customer".

export const ID_TYPE_LABELS: { [key: string]: string } = {
  nin: 'NIN',
  passport: 'Passport',
  drivers_licence: "Driver's Licence",
  voters_card: "Voter's Card",
}

export const FLAG_LABELS: { [key: string]: string } = {
  ID_SEEN_IN_OTHER_BUSINESS: 'Same ID registered by another business',
}

// Same pair the API allows ('master' level) — the detail view decrypts the
// ID number, so this matches who can review KYC documents.
export const canViewShipperIds = (profile: any) =>
  profile?.role === 'super admin' || profile?.role === 'customer service'

export const initials = (name?: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')

export interface ShipperIdentity {
  customerIdentityId: string
  fullName: string
  idType: string
  idNumber: string
  externalCustomerId: string | null
  currentVersion: number
  status: ShipperReviewStatus
  rejectionReason: string | null
  createdAt: string
  updatedAt: string
  business: { businessId: string; org_name: string; email?: string } | null
  flags: { code: string; at: string }[]
  // Check on the current image (list endpoint, trimmed); null = never checked.
  intelligentCheck?: Pick<IntelligentCheck, 'state' | 'verdict' | 'lastError'> | null
}

// Staff review of the current version. pending and approved may book;
// rejected may not until the business uploads a new image (→ pending).
export type ShipperReviewStatus = 'pending' | 'approved' | 'rejected'

// Same pill classes as KYC (kyc/KycReview.scss).
export const SHIPPER_REVIEW_META: {
  [key in ShipperReviewStatus]: { label: string; className: string }
} = {
  pending: { label: 'Pending review', className: 'pill-pending' },
  approved: { label: 'Approved', className: 'pill-approved' },
  rejected: { label: 'Rejected', className: 'pill-rejected' },
}

// identityEnforcement.documentCheck — automated check of the ID image on
// shipper ID uploads. Independent of the booking requirement above.
export const DOCUMENT_CHECK_LABELS: { [key: string]: string } = {
  off: 'Off',
  warn: 'Warn',
  required: 'Required',
}

/**
 * What a business's enforcement setting means today. 'warn' with a passed
 * date already behaves as 'required' on the API, so say so here too.
 */
export const describeEnforcement = (setting: any) => {
  const mode = setting?.mode || 'off'
  const from = setting?.requiredFrom ? new Date(setting.requiredFrom) : null
  if (mode === 'required' || (mode === 'warn' && from && from <= new Date())) {
    return { label: 'Required', className: 'active' }
  }
  if (mode === 'warn') {
    return {
      label: from
        ? `Warning · required from ${from.toLocaleDateString()}`
        : 'Warning only',
      className: 'pending',
    }
  }
  return { label: 'Not required', className: '' }
}
